import { useMemo } from 'react';
import { Conversation, useConversations } from 'src/graphql';
import { useSameRequesterConversations } from 'src/graphql/entities/conversation/use-same-requester-conversations';
import { ConditionInput, ConditionOperator } from 'src/graphql/types';
import { useOpenConversationTabs } from 'src/utils/hooks/use-open-conversation-tabs';
import { toMergeTargetSuggestion } from './suggestion-mappers';
import { MAX_SUGGESTIONS_PER_GROUP, SUGGESTION_GROUP_LABELS } from './suggestions.constants';
import {
  MergeSuggestionGroup,
  MergeSuggestionsState,
  MergeSuggestionsStatus,
  SuggestionBucket,
} from './suggestions.types';

interface UseMergeTargetSuggestionsParams {
  origin?: Conversation | null;
  enabled: boolean;
}

export const useMergeTargetSuggestions = ({
  origin,
  enabled,
}: UseMergeTargetSuggestionsParams): MergeSuggestionsState => {
  const originId = origin?.id;
  const originPartnerId = origin?.partnerId;

  const sameRequester = useSameRequesterConversations({ origin, enabled });

  const openConversationTabs = useOpenConversationTabs(originPartnerId ?? null);

  const openedEntries = useMemo(
    () => openConversationTabs.filter((entry) => entry.id !== originId),
    [openConversationTabs, originId],
  );

  const sameRequesterById = useMemo(
    () =>
      new Map(
        sameRequester.conversations.map((conversation) => [conversation.id, conversation]),
      ),
    [sameRequester.conversations],
  );

  const missingOpenedIds = useMemo(
    () =>
      openedEntries
        .filter((entry) => !sameRequesterById.has(entry.id))
        .map((entry) => entry.id),
    [openedEntries, sameRequesterById],
  );

  const resolvedSkip =
    !enabled || typeof originPartnerId !== 'number' || missingOpenedIds.length === 0;

  const resolvedFilter = useMemo<ConditionInput[]>(() => {
    if (resolvedSkip) {
      return [];
    }
    return [
      {
        attribute: 'partnerId',
        operator: ConditionOperator.Equals,
        value: String(originPartnerId),
      },
      {
        attribute: 'conversationId',
        operator: ConditionOperator.In,
        value: missingOpenedIds.join(','),
      },
    ];
  }, [resolvedSkip, originPartnerId, missingOpenedIds]);

  const {
    conversations: resolvedConversations,
    loading: resolvedLoading,
    error: resolvedError,
  } = useConversations({
    filter: resolvedFilter,
    limit: missingOpenedIds.length || 1,
    skip: resolvedSkip,
  });

  const openedConversations = useMemo<Conversation[]>(() => {
    const resolvedById = new Map(
      resolvedConversations.map((conversation) => [conversation.id, conversation]),
    );
    const result: Conversation[] = [];
    openedEntries.forEach((entry) => {
      const conversation = sameRequesterById.get(entry.id) ?? resolvedById.get(entry.id);
      if (conversation) {
        result.push(conversation);
      }
    });
    return result.slice(0, MAX_SUGGESTIONS_PER_GROUP);
  }, [openedEntries, sameRequesterById, resolvedConversations]);

  const sameRequesterConversations = useMemo<Conversation[]>(() => {
    const openedIds = new Set(openedConversations.map((conversation) => conversation.id));
    return sameRequester.conversations
      .filter((conversation) => !openedIds.has(conversation.id))
      .slice(0, MAX_SUGGESTIONS_PER_GROUP);
  }, [sameRequester.conversations, openedConversations]);

  const groups = useMemo<MergeSuggestionGroup[]>(() => {
    const result: MergeSuggestionGroup[] = [];
    if (sameRequesterConversations.length) {
      result.push({
        bucket: SuggestionBucket.SameRequester,
        label: SUGGESTION_GROUP_LABELS[SuggestionBucket.SameRequester],
        rows: sameRequesterConversations.map((conversation) =>
          toMergeTargetSuggestion(conversation, SuggestionBucket.SameRequester),
        ),
      });
    }
    if (openedConversations.length) {
      result.push({
        bucket: SuggestionBucket.CurrentlyOpened,
        label: SUGGESTION_GROUP_LABELS[SuggestionBucket.CurrentlyOpened],
        rows: openedConversations.map((conversation) =>
          toMergeTargetSuggestion(conversation, SuggestionBucket.CurrentlyOpened),
        ),
      });
    }
    return result;
  }, [sameRequesterConversations, openedConversations]);

  const status = useMemo<MergeSuggestionsStatus>(() => {
    const isLoading = sameRequester.isLoading || (!resolvedSkip && resolvedLoading);
    if (isLoading) {
      return MergeSuggestionsStatus.Loading;
    }
    if (groups.length) {
      return MergeSuggestionsStatus.Ready;
    }
    if (sameRequester.error || resolvedError) {
      return MergeSuggestionsStatus.Error;
    }
    return MergeSuggestionsStatus.Empty;
  }, [
    sameRequester.isLoading,
    sameRequester.error,
    resolvedSkip,
    resolvedLoading,
    resolvedError,
    groups.length,
  ]);

  return { status, groups };
};
